import log from '../logger';

/** The `#`-joined key `pluginPages.js` is written with. */
export const pageKey = (pluginName, pageName) => `${pluginName}#${pageName || 'default'}`;

/**
 * Turn a plugin page URL into its `pluginPages.js` entry and its props, or null. The entry's
 * `load` is the page's file; `router/pageLoading.js` makes the component from it.
 *
 * Pure, so the whole of the plugin-routing contract is testable without a router or a DOM.
 * The four binding forms the Angular templates used are reproduced here in the order they
 * were applied, which matters when two of them name the same prop:
 *
 *   1. every key on the plugin's own config, by name. This is what `plugin.pageTitle` and its
 *      45 siblings did, and it is why almost no page needs a declaration.
 *   2. `fromPlugin`, for the one page that took a plugin key under a different prop name.
 *   3. `params`, positionally. One declared name means the whole `:params` segment; more than
 *      one means split it on commas and zip.
 *   4. `props`, literal constants.
 *
 * A declared position with no corresponding argument yields `undefined`, exactly as
 * `splitParams[3]` did when the URL carried two arguments.
 */
export const resolvePluginPage = ({ plugin, pluginName, pageName, params, pages }) => {
  const entry = pages[pageKey(pluginName, pageName)];
  if (!entry) {
    // Carried over from pluginWrapperController: a plugin with no pages at all is
    // server-only by design, so landing here is a stale bookmark rather than a fault.
    const hasAnyPage = Object.keys(pages).some((k) => k.startsWith(`${pluginName}#`));
    if (hasAnyPage) {
      log.error(`Page ${pageName} not found for plugin ${pluginName}`);
    } else {
      log.debug(`Plugin ${pluginName} has no pages (server-only).`);
    }
    return null;
  }

  if (typeof entry.load !== 'function') {
    log.error(`No file to load for plugin page ${pluginName}#${pageName}`);
    return null;
  }

  const props = { ...stripFunctions(plugin) };
  Object.entries(entry.fromPlugin || {}).forEach(([prop, key]) => {
    props[prop] = plugin?.[key];
  });
  /*
   * Positional, and the split is unconditional.
   *
   * An earlier version passed the whole segment when only one name was declared, on the
   * theory that `params="params"` did not split. That is true of the templates that bound
   * `params`, but four pages bound `splitParams[0]` alone
   * (`attendance-tracking#attendanceEventAttendeePage`, `manage-app-users#appUserEditorPage`,
   * `manage-assets#appUserEditorPage`, `manage-groups#addGroup`) and those DID split. Every
   * caller passes one argument today so the two agreed by luck; the first caller to append a
   * second would have handed the page "12,34" as its id.
   *
   * `splitStyle: 'whole'` marks the pages that bound `params` directly, where the segment is
   * one opaque value that may legitimately contain commas.
   */
  const declared = entry.params || [];
  if (declared.length > 0) {
    if (entry.wholeParams) {
      props[declared[0]] = params == null ? '' : tildeDecode(params);
    } else {
      const values = (params == null ? '' : tildeDecode(params)).split(',');
      declared.forEach((name, i) => {
        props[name] = values[i];
      });
    }
  }
  Object.assign(props, entry.props || {});
  return { entry, props };
};

/**
 * The plugin config object is spread straight into props, so anything on it that is not a
 * value would end up as one. In practice `plugins.json` is JSON and this is a guard rather
 * than a fix, but a function arriving as a prop named after a config key is the kind of thing
 * that only shows up as a React warning three screens later.
 */
const stripFunctions = (plugin = {}) =>
  Object.fromEntries(Object.entries(plugin).filter(([, v]) => typeof v !== 'function'));

/**
 * ui-router state names, and the paths they mean.
 *
 * These names are not an internal detail we get to drop. The **server** emits them: every
 * built-in side-menu item in `server/app/configApi.js` carries `navigation.state`, and every
 * plugin item carries `main.plugin` with `stateParams`. The nine auth screens also call
 * `navTo('login')` and friends. So the names are a contract with code we are not changing,
 * and translating them is the right move rather than rewriting both ends.
 *
 * The list is closed and was verified against every tenant in prd, int and stg:
 * `manageAdmins`, `manageSubtenants`, `changePassword`, `exportContent`, `previewApp`,
 * `logout` and `divider` are the only built-ins any tenant uses. `manageGroups`,
 * `pushNotifications` and `previewAppLegacy` pointed at states that never existed in
 * `app.js`, were used by nobody, and were deleted from `configApi.js` in SURF-4809.
 *
 * `changePassword` the menu item was an `href`, a full page load of the standalone
 * `/changePassword` page, which has no rail. Since SURF-4828 it names `main.change-password`,
 * the same change inside the frame. The `changePassword` state below is still the standalone
 * page: the login screen sends a user with a temporary password there with `navTo`.
 */
const STATE_PATHS = {
  'main.root': () => '/main/',
  'main.manage-admins': () => '/main/manage-admins',
  'main.manage-subtenants': () => '/main/manage-subtenants',
  'main.change-password': () => '/main/change-password',
  login: () => '/login',
  changePassword: () => '/changePassword',
  changePasswordSuccess: () => '/changePasswordSuccess',
  forgotPassword: () => '/forgotPassword',
  forgotPasswordSuccess: (p) => `/forgotPasswordSuccess/${encodeURIComponent(p?.email ?? '')}`,
  resetPassword: (p) => `/resetPassword/${encodeURIComponent(p?.promptId ?? '')}`,
  resetPasswordSuccess: () => '/resetPasswordSuccess',
  register: (p) => `/register/${encodeURIComponent(p?.promptId ?? '')}`,
  registerSuccess: () => '/registerSuccess',
};

/**
 * ui-router's path-segment escaping, which is **not** `encodeURIComponent`.
 *
 * `@uirouter/angularjs` overrides the `path` param type to replace `/` with `~2F` and `~`
 * with `~~` before the usual encoding. It exists to survive proxies and servers that
 * normalise `%2F` back into a path separator, and getting it wrong is not a cosmetic
 * difference: `plugins/custom-plugins/iuoe/itrs/cms/helpers/cmsUrl.js:6` hard-codes
 * `custom-plugins~2Fiuoe~2Fitrs` into URLs that are **mailed to users**, so a CMS that only
 * understood `%2F` would answer every ITRS travel-confirmation link with a blank page.
 *
 * Three plugin names contain a slash (`custom-plugins/ubc/dues-payment`,
 * `custom-plugins/ubc/ubc-auth`, `custom-plugins/iuoe/itrs`) and `directories` puts slashes
 * in its `params`, so this is the path every one of those links takes.
 *
 * `~` is unreserved, so `encodeURIComponent` leaves the escape intact on the way out, and
 * the router's own decode leaves it intact on the way in. That is why the round trip needs
 * `tildeDecode` explicitly rather than falling out of URL decoding.
 */
const TILDE_ENCODE = { '~': '~~', '/': '~2F' };
const TILDE_DECODE = { '~~': '~', '~2F': '/' };

export const tildeEncode = (value) =>
  value == null ? value : String(value).replaceAll(/[/~]/g, (m) => TILDE_ENCODE[m]);

export const tildeDecode = (value) =>
  value == null ? value : String(value).replaceAll(/~~|~2F/g, (m) => TILDE_DECODE[m]);

/**
 * The path for a state name, or null if the name is unknown.
 *
 * `main.plugin` builds `/main/plugin/<plugin>/<page>/<params>`. The trailing slash on an
 * empty `params` is deliberate and is what ui-router produced, so links the rail compares
 * against keep their shape.
 */
export const pathForState = (stateName, stateParams = {}) => {
  if (stateName === 'main.plugin') {
    const plugin = tildeEncode(stateParams.pluginName ?? '');
    const page = tildeEncode(stateParams.pageName || 'default');
    const params = stateParams.params == null ? '' : tildeEncode(stateParams.params);
    return `/main/plugin/${plugin}/${page}/${params}`;
  }
  const build = STATE_PATHS[stateName];
  if (!build) {
    log.error(`No path for state "${stateName}"`);
    return null;
  }
  return build(stateParams);
};

/** Every state name the router answers to. Exported so a test can pin the list. */
export const knownStates = () => ['main.plugin', ...Object.keys(STATE_PATHS)];

/**
 * `decodeURIComponent` that yields the raw segment instead of throwing.
 *
 * A lone `%`, or a `%` followed by anything that is not two hex digits, makes
 * `decodeURIComponent` throw `URIError`. This runs inside `AppFrame`'s render, and the CMS has
 * no error boundary, so a throw here paints a blank page with no way back.
 *
 * The router will not *match* such a path, so the entry case is already covered: the URL falls
 * to `defaultNotFoundComponent` and redirects home. What is not covered is a client-side
 * transition into one, where the frame can re-render against the new pathname before the
 * not-found tree replaces it. Returning the segment undecoded degrades to "the rail does not
 * highlight", which is the right failure for a URL that is about to be redirected anyway.
 */
const safeDecode = (value) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/**
 * The state name and params a path corresponds to. The inverse of `pathForState`.
 *
 * The rail needs this. `sidebar/definitions.js` matches the current route against menu items
 * four ways, and its last branch, `item.navigation.state === stateName` with a deep compare of
 * `stateParams`, is **the only one** that ever matched Manage Admins, Manage Subtenants, or a
 * plugin whose name contains a slash. Passing an empty `stateName` silently unhighlights all
 * three, which is what a first pass at this did.
 */
export const stateForPath = (path = '') => {
  const plugin = /^\/main\/plugin\/([^/]+)\/([^/]+)(?:\/(.*))?$/.exec(path);
  if (plugin) {
    const [, pluginName, pageName, params = ''] = plugin;
    return {
      stateName: 'main.plugin',
      stateParams: {
        pluginName: tildeDecode(safeDecode(pluginName)),
        pageName: tildeDecode(safeDecode(pageName)),
        params: tildeDecode(safeDecode(params)),
      },
    };
  }
  // `/main/` and `/main` both mean the home state, as ui-router's squashed index did.
  const normalised = path.replace(/\/$/, '') || '/main';
  const match = Object.keys(STATE_PATHS).find((name) => {
    const built = STATE_PATHS[name]({});
    return built.replace(/\/$/, '') === normalised;
  });
  return { stateName: match || '', stateParams: {} };
};
